import { useEffect, useState } from "react";
import { SenseCard } from "../components/SenseCard";
import { copy } from "../copy";
import { platformOf } from "../domain/accounts";
import { topicTitle } from "../domain/display";
import { formatNumber, formatWhen } from "../lib/format";
import {
  breakdownFilled,
  currentGuide,
  emptyGuide,
  guideFields,
  guideIsEmpty,
  manualBenchmark,
  passFor,
  type BreakdownFields,
  type GuideFields,
  type NoteError,
} from "../domain/research";
import { typeCovered } from "../domain/executors";
import { topicsForAccount } from "../domain/selectors";
import { taskStatusLine, tasksForAccount } from "../domain/tasks";
import { useApp } from "../state/store";
import type { BenchmarkNote } from "../types";
import { DISTRIBUTION_PLATFORMS } from "../types";
const QUICK_COUNTS = [5, 10, 20];
const ERRORS: Record<NoteError, string> = {
  title: copy.research.needTitle,
  date: copy.research.needDate,
  link: copy.research.badLink,
  cover: copy.research.badCover,
  count: copy.research.badCount,
};

type Part = "notes" | "style" | "score";

export function Research() {
  const app = useApp();
  const part = partFrom(app.href);
  return (
    <div className="stack">
      <div className="status-tabs" role="tablist">
        {(["notes", "style", "score"] as const).map((id) => (
          <button key={id} type="button" role="tab" aria-selected={part === id} onClick={() => app.navigate("research", id === "notes" ? "" : `part=${id}`)}>
            {copy.research.tabs[id]}
          </button>
        ))}
      </div>
      {part === "notes" ? <Notes /> : null}
      {part === "style" ? <StyleGuide /> : null}
      {part === "score" ? <Scores /> : null}
    </div>
  );
}

function partFrom(href: string): Part {
  const value = new URLSearchParams(href.split("?")[1] ?? "").get("part");
  if (value === "style" || value === "score") return value;
  return "notes";
}

function Notes() {
  const app = useApp();
  const [count, setCount] = useState(5);
  const [keywords, setKeywords] = useState("");
  const [adding, setAdding] = useState(false);
  const notes = (app.state.benchmarks ?? [])
    .filter((item) => item.accountId === app.accountId && item.deletedAt == null)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  const hunts = tasksForAccount(app.state.tasks ?? [], app.accountId)
    .filter((task) => task.type === "找对标爆款")
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 5);

  function ask() {
    const platform = platformOf(app.state.accounts ?? [], app.accountId);
    app.createTask({
      type: "找对标爆款",
      platforms: [platform],
      direction: keywords.trim(),
      count,
    });
  }

  return (
    <>
      <section className="panel find-form">
        <div className="find-count">
          <span>{copy.research.findCount}</span>
          <div className="count-picks">
            {QUICK_COUNTS.map((value) => (
              <button key={value} type="button" className={count === value ? "button line" : "button ghost"} aria-pressed={count === value} onClick={() => setCount(value)}>
                {value}
              </button>
            ))}
            <input
              type="number"
              min={1}
              max={100}
              aria-label={copy.research.findCount}
              value={Number.isFinite(count) ? count : ""}
              onChange={(event) => setCount(event.target.value === "" ? 0 : Number(event.target.value))}
            />
          </div>
        </div>
        <label>
          {copy.research.keywords}
          <input value={keywords} maxLength={400} placeholder={copy.research.keywordPlaceholder} onChange={(event) => setKeywords(event.target.value)} />
        </label>
        <div className="find-submit">
          <button type="button" className="button primary" onClick={ask}>
            {copy.research.findSubmit}
          </button>
          <button type="button" className="button line" onClick={() => setAdding((open) => !open)}>
            {adding ? copy.research.cancel : copy.research.add}
          </button>
        </div>
      </section>
      <p className="muted-banner">{copy.research.seen}</p>
      {adding ? <AddNote onDone={() => setAdding(false)} /> : null}
      {notes.length ? (
        <section className="panel benchmark-list">
          {notes.map((note) => (
            <BenchmarkRow key={note.id} note={note} />
          ))}
        </section>
      ) : (
        <p className="muted">{copy.research.emptyNotes}</p>
      )}
      {hunts.length ? (
        <section className="stack">
          <h2 className="section-label">{copy.research.recent}</h2>
          {hunts.map((task) => (
            <article key={task.id} className="panel find-card">
              <strong>{task.direction || task.type}</strong>
              <p className="muted">{taskStatusLine(task, app.agentReady, typeCovered(app.state.executors ?? [], task.type))}</p>
            </article>
          ))}
        </section>
      ) : null}
    </>
  );
}

function AddNote({ onDone }: { onDone: () => void }) {
  const app = useApp();
  const [draft, setDraft] = useState({
    link: "",
    platform: "小红书",
    title: "",
    author: "",
    likes: "",
    collects: "",
    comments: "",
    recordedDate: "",
    coverUrl: "",
    note: "",
  });
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function save() {
    const checked = manualBenchmark(draft, app.accountId, new Date().toISOString(), "check");
    if ("error" in checked) {
      setError(ERRORS[checked.error]);
      return;
    }
    const saved = app.saveBenchmark(draft);
    if (!saved) return;
    onDone();
  }

  return (
    <section className="panel">
      <div className="form-grid">
        <label className="wide">
          {copy.research.fields.title}
          <input value={draft.title} maxLength={300} onChange={(event) => set("title", event.target.value)} />
        </label>
        <label className="wide">
          {copy.research.fields.link}
          <input value={draft.link} maxLength={1000} placeholder="https://" onChange={(event) => set("link", event.target.value)} />
        </label>
        <label>
          {copy.research.fields.platform}
          <select value={draft.platform} onChange={(event) => set("platform", event.target.value)}>
            {DISTRIBUTION_PLATFORMS.map((label) => (
              <option key={label}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          {copy.research.fields.author}
          <input value={draft.author} maxLength={80} onChange={(event) => set("author", event.target.value)} />
        </label>
        <label>
          {copy.research.fields.likes}
          <input inputMode="numeric" value={draft.likes} onChange={(event) => set("likes", event.target.value)} />
        </label>
        <label>
          {copy.research.fields.collects}
          <input inputMode="numeric" value={draft.collects} onChange={(event) => set("collects", event.target.value)} />
        </label>
        <label>
          {copy.research.fields.comments}
          <input inputMode="numeric" value={draft.comments} onChange={(event) => set("comments", event.target.value)} />
        </label>
        <label>
          {copy.research.fields.recorded}
          <input type="date" value={draft.recordedDate} onChange={(event) => set("recordedDate", event.target.value)} />
        </label>
        <label className="wide">
          {copy.research.fields.cover}
          <input value={draft.coverUrl} maxLength={1000} placeholder="https://" onChange={(event) => set("coverUrl", event.target.value)} />
        </label>
        <label className="wide">
          {copy.research.fields.note}
          <textarea rows={3} maxLength={2000} value={draft.note} onChange={(event) => set("note", event.target.value)} />
        </label>
      </div>
      {error ? <p className="inline-error">{error}</p> : null}
      <button type="button" className="button line" onClick={save}>
        {copy.research.saveNote}
      </button>
    </section>
  );
}

function BenchmarkRow({ note }: { note: BenchmarkNote }) {
  const app = useApp();
  const [fields, setFields] = useState<BreakdownFields>(() => breakdownOf(note));

  useEffect(() => {
    setFields(breakdownOf(note));
  }, [note]);

  function ask() {
    app.createTask({
      type: "拆解对标",
      subjectId: note.id,
      platforms: [note.platform],
      direction: note.title,
      count: 1,
    });
  }

  return (
    <article className="benchmark-row">
      {note.coverUrl.startsWith("https://") ? <Cover url={note.coverUrl} title={note.title} /> : null}
      <div className="benchmark-copy">
        <div className="benchmark-head">
          <h3>{note.title}</h3>
          <button type="button" className="button line" onClick={ask}>
            {copy.research.askBreakdown}
          </button>
        </div>
        <p className="benchmark-meta">
          <span>{metaLine(note)}</span>
          {safeLink(note.link) ? (
            <a href={note.link} target="_blank" rel="noreferrer">
              {copy.research.fields.link}
            </a>
          ) : null}
        </p>
        {note.note ? <p className="muted">{note.note}</p> : null}
        <details className="breakdown">
          <summary>{breakdownFilled(note) ? copy.research.breakdownDone : copy.research.breakdown}</summary>
          <div className="form-grid">
            {(Object.keys(fields) as Array<keyof BreakdownFields>).map((key) => (
              <label key={key} className="wide">
                {copy.research.fields[key]}
                <textarea rows={2} maxLength={2000} value={fields[key]} onChange={(event) => setFields((current) => ({ ...current, [key]: event.target.value }))} />
              </label>
            ))}
          </div>
          <button type="button" className="button line" onClick={() => app.saveBreakdown(note.id, fields)}>
            {copy.research.saveBreakdown}
          </button>
        </details>
      </div>
    </article>
  );
}

function Cover({ url, title }: { url: string; title: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return <img className="benchmark-cover" src={url} alt={title} onError={() => setFailed(true)} />;
}

function StyleGuide() {
  const app = useApp();
  const guide = currentGuide(app.state.styleGuides ?? [], app.accountId);
  const [fields, setFields] = useState<GuideFields>(() => guideFields(guide));
  const [loaded, setLoaded] = useState(guide?.id ?? "");
  const history = (app.state.styleGuides ?? [])
    .filter((item) => item.accountId === app.accountId)
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));

  useEffect(() => {
    const id = guide?.id ?? "";
    if (id === loaded) return;
    setFields(guideFields(guide));
    setLoaded(id);
  }, [guide, loaded]);

  return (
    <>
      <section className="panel stack">
        {(Object.keys(emptyGuide()) as Array<keyof GuideFields>).map((key) => (
          <label key={key}>
            {copy.research.fields[key]}
            <textarea rows={3} maxLength={2000} value={fields[key]} onChange={(event) => setFields((current) => ({ ...current, [key]: event.target.value }))} />
          </label>
        ))}
        <button type="button" className="button primary" disabled={guideIsEmpty(fields)} onClick={() => app.saveStyleVersion(fields)}>
          {copy.research.styleSave}
        </button>
      </section>
      {history.length ? (
        <section className="stack">
          <h2 className="section-label">{copy.research.history}</h2>
          {history.map((version) => (
            <article key={version.id} className="panel guide-row">
              <div>
                <strong>{formatWhen(version.createdAt)}</strong>
                {version.current ? <em className="tag">{copy.research.current}</em> : null}
              </div>
              {version.current ? null : (
                <button type="button" className="button line" onClick={() => app.useStyleVersion(version.id)}>
                  {copy.research.useCurrent}
                </button>
              )}
            </article>
          ))}
        </section>
      ) : (
        <p className="muted">{copy.research.styleEmpty}</p>
      )}
    </>
  );
}

function Scores() {
  const app = useApp();
  const line = passFor(app.state.scoreSettings ?? [], app.accountId);
  const [draft, setDraft] = useState(line);
  const drafts = topicsForAccount(app.state.topics, app.accountId).filter((topic) => topic.status === "待审阅" && !topic.inTrash);

  useEffect(() => {
    setDraft(line);
  }, [line]);

  return (
    <div className="score-list stack">
      <section className="panel">
        <div className="find-count">
          <span>{copy.research.pass}</span>
          <div className="count-picks">
            <input
              type="number"
              min={0}
              max={40}
              aria-label={copy.research.pass}
              value={Number.isFinite(draft) ? draft : ""}
              onChange={(event) => setDraft(event.target.value === "" ? 0 : Number(event.target.value))}
            />
            <span>{copy.research.passHint}</span>
            <button type="button" className="button line" onClick={() => app.setPassTotal(draft)}>
              {copy.research.savePass}
            </button>
          </div>
        </div>
      </section>
      {drafts.length ? (
        drafts.map((topic) => (
          <article key={topic.id} className="panel score-row">
            <button type="button" className="text-action" onClick={() => app.openReview(topic.id)}>
              {topicTitle(topic)}
            </button>
            <SenseCard topicId={topic.id} />
          </article>
        ))
      ) : (
        <p className="muted">{copy.research.emptyScore}</p>
      )}
    </div>
  );
}

function breakdownOf(note: BenchmarkNote): BreakdownFields {
  return {
    titlePattern: note.titlePattern,
    coverType: note.coverType,
    bodyStructure: note.bodyStructure,
    aiTools: note.aiTools,
    interaction: note.interaction,
    whyViral: note.whyViral,
  };
}

function seen(value: number | null): string {
  return value == null ? copy.dash : formatNumber(value);
}

function metaLine(note: BenchmarkNote): string {
  return [
    note.platform || "小红书",
    note.author,
    `${copy.research.recorded} ${note.recordedDate}`,
    `${copy.research.fields.likes} ${seen(note.likes)}`,
    `${copy.research.fields.collects} ${seen(note.collects)}`,
    `${copy.research.fields.comments} ${seen(note.comments)}`,
  ]
    .filter((part) => part.trim())
    .join(" · ");
}

function safeLink(value: string): boolean {
  return value.startsWith("https://") || value.startsWith("http://");
}
